import type { TypedSupabaseClient } from '../client';
import type {
  IUserPreferencesRepository,
  ResolvedUserPreference,
  UserPreferenceSource,
} from '../../../interfaces/src';

/**
 * Toute la logique de couche (personnel / défaut client / vue admin) vit dans
 * les RPC `get_user_preference`, `set_user_preference` et
 * `clear_user_preference` (20260907130000_preferences_per_tenant.sql) : le
 * tenant courant et la vue client de l'admin plateforme ne se lisent que
 * côté serveur. La table legacy `user_preferences` n'est plus touchée ici.
 */
export class SupabaseUserPreferencesRepository implements IUserPreferencesRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async resolve(key: string): Promise<ResolvedUserPreference> {
    const { data, error } = await this.supabase.rpc(
      'get_user_preference' as never,
      { p_key: key } as never,
    );
    if (error) throw error;
    const row = data as { value?: unknown; source?: UserPreferenceSource } | null;
    if (!row || typeof row !== 'object') return { value: null, source: null };
    return { value: row.value ?? null, source: row.source ?? null };
  }

  async get(key: string): Promise<unknown | null> {
    return (await this.resolve(key)).value;
  }

  async set(key: string, value: unknown): Promise<void> {
    const { error } = await this.supabase.rpc(
      'set_user_preference' as never,
      { p_key: key, p_value: value } as never,
    );
    if (error) throw error;
  }

  async remove(key: string): Promise<void> {
    const { error } = await this.supabase.rpc(
      'clear_user_preference' as never,
      { p_key: key } as never,
    );
    if (error) throw error;
  }
}
